import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { immediateToast } from "izitoast-react";
import { StoreDispatch } from "../store";
import {
  createWorkflow,
  createWorkflowTemplate,
  CreateWorkflowInput,
  searchWorkflowTemplates,
  Workflow
} from "src/network/graphql/workflowService";

interface WorkflowNewState {
  saveLoading: boolean;
  templates: Workflow[];
  templatesLoading: boolean;
  templatesError: string | null;
}

const initialState: WorkflowNewState = {
  saveLoading: false,
  templates: [],
  templatesLoading: false,
  templatesError: null
};

const workflowNewSlice = createSlice({
  name: "workflowNew",
  initialState,
  reducers: {
    saveStart: (state) => {
      state.saveLoading = true;
    },
    saveFinish: (state) => {
      state.saveLoading = false;
    },
    templatesFetchStart: (state) => {
      state.templatesLoading = true;
      state.templatesError = null;
    },
    templatesFetchSuccess: (state, action: PayloadAction<Workflow[]>) => {
      state.templatesLoading = false;
      state.templates = action.payload;
    },
    templatesFetchFail: (state, action: PayloadAction<string>) => {
      state.templatesLoading = false;
      state.templates = [];
      state.templatesError = action.payload;
    }
  }
});

export const {
  saveStart,
  saveFinish,
  templatesFetchStart,
  templatesFetchSuccess,
  templatesFetchFail
} = workflowNewSlice.actions;

export const createWorkflowAction = (input: CreateWorkflowInput, cb?: (_workflow: Workflow) => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(saveStart());
    try {
      const response = await createWorkflow(input);
      const workflow: Workflow | undefined = response?.data?.create_workflow;

      dispatch(saveFinish());
      if (workflow) {
        immediateToast("success", {
          message: "Workflow created",
          timeout: 3000,
          position: "topCenter"
        });
        cb?.(workflow);
      }
      return workflow ?? null;
    } catch (error) {
      dispatch(saveFinish());
      return null;
    }
  };
};

export const createWorkflowTemplateAction = (input: CreateWorkflowInput, cb?: (_workflow: Workflow) => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(saveStart());
    try {
      const response = await createWorkflowTemplate(input);
      const workflow: Workflow | undefined = response?.data?.internal_create_workflow_template;

      dispatch(saveFinish());
      if (workflow) {
        immediateToast("success", {
          message: "Workflow template created",
          timeout: 3000,
          position: "topCenter"
        });
        cb?.(workflow);
      }
      return workflow ?? null;
    } catch (error) {
      dispatch(saveFinish());
      return null;
    }
  };
};

export const fetchWorkflowTemplatesForCloneAction = () => {
  return async (dispatch: StoreDispatch) => {
    dispatch(templatesFetchStart());
    try {
      const response = await searchWorkflowTemplates();
      const items: Workflow[] = response?.data?.search_workflow?.items ?? [];

      dispatch(templatesFetchSuccess(items));
      return items;
    } catch (error) {
      const message = (error as Error)?.message || "Failed to load workflow templates";

      dispatch(templatesFetchFail(message));
      immediateToast("error", {
        message,
        timeout: 3000,
        position: "topCenter"
      });
      return null;
    }
  };
};

export default workflowNewSlice.reducer;
